import React from 'react';
import {
  Box, Flex, Text, Badge, IconButton, Card, CardBody,
  Accordion, AccordionItem, AccordionButton, AccordionPanel,
  AccordionIcon, VStack, Icon, Input, InputGroup, InputLeftElement,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

export interface FilterSection {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}

export interface FilterSidebarProps {
  sections: FilterSection[];
  searchInput?: string;
  onSearchChange?: (value: string) => void;
  onClose: () => void;
  onClearAll: () => void;
}

function FilterItem({ label, isSelected, onClick }: {
  label: string; isSelected: boolean; onClick: () => void;
}) {
  return (
    <Flex
      align="center"
      px={3}
      py={1.5}
      borderRadius="8px"
      cursor="pointer"
      onClick={onClick}
      bg={isSelected ? 'brand.50' : 'transparent'}
      color={isSelected ? 'brand.500' : 'gray.600'}
      fontWeight={isSelected ? '600' : '400'}
      _hover={{ bg: isSelected ? 'brand.100' : 'gray.50' }}
      transition="all 0.15s"
    >
      <Box
        w="14px"
        h="14px"
        borderRadius="3px"
        mr={3}
        flexShrink={0}
        bg={isSelected ? 'brand.500' : 'transparent'}
        border="2px solid"
        borderColor={isSelected ? 'brand.500' : 'gray.300'}
        transition="all 0.15s"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {isSelected && (
          <Icon viewBox="0 0 12 12" w="8px" h="8px" color="white">
            <path d="M2.5 6l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </Icon>
        )}
      </Box>
      <Text fontSize="sm">{label}</Text>
    </Flex>
  );
}

function FilterSummary({ items }: { items: string[] }) {
  if (items.length < 2) return null;
  const bullet = String.fromCharCode(8226);
  const display = items.length <= 3
    ? items.join(` ${bullet} `)
    : `${items.slice(0, 2).join(` ${bullet} `)} +${items.length - 2}`;
  return (
    <Text fontSize="xs" color="gray.400" fontWeight="400" mt={0.5} noOfLines={1}>
      {display}
    </Text>
  );
}

function SearchIcon() {
  return (
    <Icon viewBox="0 0 16 16" w={4} h={4} color="gray.400">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="M11 11l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    </Icon>
  );
}

function CloseIcon() {
  return (
    <Icon viewBox="0 0 16 16" w={3.5} h={3.5}>
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    </Icon>
  );
}

export function FilterSidebar({
  sections,
  searchInput,
  onSearchChange,
  onClose,
  onClearAll,
}: FilterSidebarProps) {
  const { t } = useTranslation();
  const activeCount = sections.reduce((sum, s) => sum + s.selected.length, 0);

  return (
    <Card
      w="280px"
      minW="280px"
      borderRadius="20px"
      boxShadow="0px 18px 40px rgba(112, 144, 176, 0.12)"
      alignSelf="flex-start"
      position="sticky"
      top="24px"
      data-testid="filter-sidebar"
    >
      <CardBody px={4} py={5}>
        {/* Header */}
        <Flex justify="space-between" align="center" mb={4}>
          <Flex align="center" gap={2}>
            <Text fontSize="lg" fontWeight="700" color="gray.700">
              {t('admin.library.filters.title', 'Filters')}
            </Text>
            {activeCount > 0 && (
              <Badge
                colorScheme="brand"
                borderRadius="full"
                px={2.5}
                py={0.5}
                fontSize="xs"
                fontWeight="600"
                cursor="pointer"
                onClick={onClearAll}
                data-testid="filter-clear-badge"
              >
                {activeCount} ×
              </Badge>
            )}
          </Flex>
          <IconButton
            aria-label={t('admin.library.filters.close', 'Close filters')}
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            color="gray.400"
            _hover={{ color: 'gray.600', bg: 'gray.50' }}
            onClick={onClose}
            data-testid="filter-sidebar-close"
          />
        </Flex>

        {/* Search */}
        {onSearchChange !== undefined && (
          <InputGroup size="sm" mb={4}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon />
            </InputLeftElement>
            <Input
              placeholder={t('common.search')}
              value={searchInput ?? ''}
              onChange={(e) => onSearchChange(e.target.value)}
              borderRadius="10px"
              data-testid="filter-search-input"
              aria-label={t('common.search')}
            />
          </InputGroup>
        )}

        {/* Filter sections */}
        <Accordion allowMultiple defaultIndex={sections.map((_, i) => i)}>
          {sections.map((section) => (
            <AccordionItem key={section.key} border="none" data-testid={`filter-section-${section.key}`}>
              <AccordionButton px={2} py={2} borderRadius="8px" _hover={{ bg: 'gray.50' }}>
                <Flex flex="1" direction="column" align="flex-start">
                  <Text fontSize="sm" fontWeight="600" color="gray.700">
                    {section.label}
                  </Text>
                  <FilterSummary items={section.selected} />
                </Flex>
                {section.selected.length > 0 && (
                  <Badge colorScheme="brand" borderRadius="full" fontSize="2xs" mr={1}>
                    {section.selected.length}
                  </Badge>
                )}
                <AccordionIcon color="gray.400" />
              </AccordionButton>
              <AccordionPanel px={0} py={1}>
                <VStack align="stretch" spacing={0} maxH="200px" overflowY="auto">
                  {section.options.map((option) => (
                    <FilterItem
                      key={option.value}
                      label={option.label}
                      isSelected={section.selected.includes(option.value)}
                      onClick={() => section.onToggle(option.value)}
                    />
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </CardBody>
    </Card>
  );
}
